/**
 * Modèle de rendu des notifications de l'espace électricien.
 *
 * Le serveur n'écrit que des FAITS dans `metadata` (montants, codes, compteurs).
 * Ce module les transforme en un descriptif d'affichage purement structurel :
 * une icône, une teinte, des CLÉS i18n avec leurs variables, et des puces de
 * chiffres. Il ne traduit rien et ne formate aucun montant — c'est la couche UI
 * qui appelle `t()` et `formatTND`, parce que la langue de l'électricien peut
 * changer bien après l'écriture de la notification.
 *
 * Même descriptif pour le web (`apps/erp-pro`) et le mobile
 * (`apps/tunielec-pro-mobile`) : les deux surfaces rendent la même chose.
 */

import type { CommandeStatus } from './types';

// ------------------------------------------------------------------
// Types
// ------------------------------------------------------------------

export const PRO_NOTIFICATION_TYPES = [
  'devis_created',
  'devis_validated',
  'devis_rejected',
  'commande_created',
  'commande_status',
  'commission_approved',
  'commission_rejected',
  'monthly_recap',
  'clients_digest',
  'formation_video_published',
  'product_published',
  'product_requests_resolved',
] as const;

export type ProNotificationKind = (typeof PRO_NOTIFICATION_TYPES)[number];

/**
 * Familles d'affichage : la cloche propose un filtre par famille et le mobile
 * s'en sert pour ses sections.
 */
export type ProNotificationCategory =
  | 'documents'
  | 'money'
  | 'insights'
  | 'catalog';

export type ProNotificationTone =
  | 'brand'
  | 'success'
  | 'danger'
  | 'money'
  | 'info'
  | 'learning'
  | 'neutral';

/** Clé d'icône stable — chaque surface la mappe vers sa propre bibliothèque. */
export type ProNotificationIcon =
  | 'quote'
  | 'quote-check'
  | 'quote-x'
  | 'order'
  | 'order-check'
  | 'packed'
  | 'shipped'
  | 'delivered'
  | 'returned'
  | 'rejected'
  | 'commission-in'
  | 'commission-out'
  | 'chart'
  | 'users'
  | 'video'
  | 'new-product'
  | 'inbox';

/** Une chaîne à traduire : clé + repli + variables NOMMÉES. */
export interface ProNotificationL10n {
  key: string;
  fallback: string;
  vars?: Record<string, string | number>;
}

/** Puce de chiffre affichée sous le texte — le « visuel » de la notification. */
export interface ProNotificationStat {
  id: string;
  label: ProNotificationL10n;
  /** `money` → formatTND, `count` → entier, `percent` → +/-x %, `text` → brut. */
  kind: 'money' | 'count' | 'percent' | 'text';
  value: number | string;
  tone?: ProNotificationTone;
  trend?: 'up' | 'down' | 'flat';
}

/** Parcours d'une commande, pour dessiner le petit stepper. */
export interface ProNotificationOrderProgress {
  status: CommandeStatus;
  /** Index 0-based dans le parcours nominal, `null` si hors parcours. */
  step: number | null;
  total: number;
  /** `true` pour retournee / rejetee : le parcours s'est arrêté. */
  interrupted: boolean;
}

export interface ProNotificationView {
  type: string;
  kind: ProNotificationKind | null;
  category: ProNotificationCategory;
  tone: ProNotificationTone;
  icon: ProNotificationIcon;
  title: ProNotificationL10n;
  body: ProNotificationL10n | null;
  stats: ProNotificationStat[];
  orderProgress: ProNotificationOrderProgress | null;
  /** Montant mis en avant, en grand. `direction` colore le signe. */
  amount: { value: number; direction: 'in' | 'out' | 'neutral' } | null;
  thumbnailUrl: string | null;
  /**
   * Période couverte par un bilan. L'UI en dérive un libellé LOCALISÉ
   * (« juillet 2026 », « يوليو 2026 ») et le réinjecte dans la variable
   * `period` du corps — le serveur ne peut pas l'écrire sans figer la langue.
   */
  period: ProNotificationPeriod | null;
}

/** `start` est le premier jour de la période, en ISO `YYYY-MM-DD`. */
export interface ProNotificationPeriod {
  start: string;
  granularity: 'month' | 'week';
}

/**
 * Forme minimale attendue en entrée — structurelle exprès, pour que le domaine
 * ne dépende pas des DTO de `data-access`.
 */
export interface ProNotificationLike {
  type: string;
  title: string;
  body?: string | null;
  metadata?: Record<string, unknown> | null;
}

// ------------------------------------------------------------------
// Parcours commande
// ------------------------------------------------------------------

/** Parcours nominal d'une commande. `retournee` / `rejetee` en sortent. */
export const COMMANDE_FLOW: ReadonlyArray<CommandeStatus> = [
  'en_attente',
  'confirmee',
  'emballee',
  'expediee',
  'livree',
];

export function commandeFlowStep(status: CommandeStatus): number | null {
  const i = COMMANDE_FLOW.indexOf(status);
  return i === -1 ? null : i;
}

const COMMANDE_STATUS_ICON: Record<CommandeStatus, ProNotificationIcon> = {
  en_attente: 'order',
  confirmee: 'order-check',
  emballee: 'packed',
  expediee: 'shipped',
  livree: 'delivered',
  retournee: 'returned',
  rejetee: 'rejected',
};

const COMMANDE_STATUS_TONE: Record<CommandeStatus, ProNotificationTone> = {
  en_attente: 'neutral',
  confirmee: 'info',
  emballee: 'info',
  expediee: 'brand',
  livree: 'success',
  retournee: 'neutral',
  rejetee: 'danger',
};

const CATEGORY_BY_KIND: Record<ProNotificationKind, ProNotificationCategory> = {
  devis_created: 'documents',
  devis_validated: 'documents',
  devis_rejected: 'documents',
  commande_created: 'documents',
  commande_status: 'documents',
  commission_approved: 'money',
  commission_rejected: 'money',
  monthly_recap: 'insights',
  clients_digest: 'insights',
  formation_video_published: 'catalog',
  product_published: 'catalog',
  product_requests_resolved: 'documents',
};

// ------------------------------------------------------------------
// Rendu
// ------------------------------------------------------------------

/**
 * Décrit une notification. Ne lève jamais : un type inconnu ou une `metadata`
 * incomplète retombe sur le texte français écrit par le serveur, pour qu'une
 * notification d'une version plus récente du backend reste lisible.
 */
export function describeProNotification(
  notification: ProNotificationLike,
): ProNotificationView {
  const meta = notification.metadata ?? {};
  const kind = isKnownKind(notification.type) ? notification.type : null;
  const base: ProNotificationView = {
    type: notification.type,
    kind,
    category: kind ? CATEGORY_BY_KIND[kind] : 'documents',
    tone: 'neutral',
    icon: 'inbox',
    title: { key: '', fallback: notification.title },
    body: notification.body ? { key: '', fallback: notification.body } : null,
    stats: [],
    orderProgress: null,
    amount: null,
    thumbnailUrl: null,
    period: null,
  };

  switch (kind) {
    case 'devis_created':
      return {
        ...base,
        tone: 'info',
        icon: 'quote',
        title: k('pro.notif.devis_created.title', 'Nouveau devis créé'),
        body: k('pro.notif.devis_created.body', 'Devis {{code}} enregistré.', {
          code: str(meta['code']) ?? '',
        }),
        stats: compact([
          moneyStat('total', 'pro.notif.stat.total', 'Total TTC', num(meta['totalTtc'])),
          countStat('lines', 'pro.notif.stat.lines', 'Lignes', num(meta['linesCount'])),
        ]),
      };

    case 'devis_validated':
      return {
        ...base,
        tone: 'success',
        icon: 'quote-check',
        title: k('pro.notif.devis_validated.title', 'Devis validé'),
        body: k(
          'pro.notif.devis_validated.body',
          'Le devis {{code}} est validé, vous pouvez le transformer en commande.',
          { code: str(meta['code']) ?? '' },
        ),
        stats: compact([
          moneyStat('total', 'pro.notif.stat.total', 'Total TTC', num(meta['totalTtc'])),
          moneyStat(
            'commission',
            'pro.notif.stat.commission',
            'Commission',
            num(meta['commission']),
            'money',
          ),
        ]),
      };

    case 'devis_rejected':
      return {
        ...base,
        tone: 'danger',
        icon: 'quote-x',
        title: k('pro.notif.devis_rejected.title', 'Devis rejeté'),
        body: k('pro.notif.devis_rejected.body', 'Le devis {{code}} a été rejeté.', {
          code: str(meta['code']) ?? '',
        }),
        stats: compact([
          textStat('reason', 'pro.notif.stat.reason', 'Motif', str(meta['reason'])),
        ]),
      };

    case 'commande_created':
      return {
        ...base,
        tone: 'brand',
        icon: 'order',
        title: k('pro.notif.commande_created.title', 'Commande passée'),
        body: k('pro.notif.commande_created.body', 'Commande {{code}} enregistrée.', {
          code: str(meta['code']) ?? '',
        }),
        orderProgress: progress('en_attente'),
        stats: compact([
          moneyStat('total', 'pro.notif.stat.total', 'Total TTC', num(meta['totalTtc'])),
          moneyStat(
            'commission',
            'pro.notif.stat.commission_pending',
            'Commission à venir',
            num(meta['commission']),
            'money',
          ),
        ]),
      };

    case 'commande_status': {
      const status = asCommandeStatus(meta['status']);
      if (!status) return base;
      return {
        ...base,
        tone: COMMANDE_STATUS_TONE[status],
        icon: COMMANDE_STATUS_ICON[status],
        title: k(
          `pro.notif.commande_status.${status}.title`,
          COMMANDE_STATUS_FALLBACK[status].title,
        ),
        body: k(
          `pro.notif.commande_status.${status}.body`,
          COMMANDE_STATUS_FALLBACK[status].body,
          { code: str(meta['code']) ?? '' },
        ),
        orderProgress: progress(status),
        stats: compact([
          moneyStat('total', 'pro.notif.stat.total', 'Total TTC', num(meta['totalTtc'])),
          moneyStat(
            'commission',
            'pro.notif.stat.commission',
            'Commission',
            num(meta['commission']),
            'money',
          ),
        ]),
      };
    }

    case 'commission_approved':
      return {
        ...base,
        tone: 'money',
        icon: 'commission-in',
        title: k('pro.notif.commission_approved.title', 'Commission approuvée'),
        body: refBody(meta, 'pro.notif.commission_approved.body', 'Commission de la commande {{ref}} créditée sur votre portefeuille.'),
        amount: amountOf(meta['amount'], 'in'),
        stats: compact([
          moneyStat(
            'balance',
            'pro.notif.stat.balance',
            'Nouveau solde',
            num(meta['balanceAfter']),
            'money',
          ),
        ]),
      };

    case 'commission_rejected':
      return {
        ...base,
        tone: 'danger',
        icon: 'commission-out',
        title: k('pro.notif.commission_rejected.title', 'Commission rejetée'),
        body: refBody(meta, 'pro.notif.commission_rejected.body', 'La commission de la commande {{ref}} n’a pas été retenue.'),
        amount: amountOf(meta['amount'], 'out'),
        stats: compact([
          moneyStat(
            'balance',
            'pro.notif.stat.balance',
            'Solde inchangé',
            num(meta['balanceAfter']),
            'neutral',
          ),
        ]),
      };

    case 'monthly_recap': {
      const commission = num(meta['commission']) ?? 0;
      const previous = num(meta['previousCommission']);
      return {
        ...base,
        tone: 'money',
        icon: 'chart',
        title: k('pro.notif.monthly_recap.title', 'Votre bilan du mois'),
        body: k(
          'pro.notif.monthly_recap.body',
          'Vous avez gagné {{amount}} en commissions sur {{period}}.',
          { amount: formatDtFallback(commission), period: str(meta['period']) ?? '' },
        ),
        amount: { value: commission, direction: 'in' },
        period: periodOf(meta['periodStart'], 'month'),
        stats: compact([
          moneyStat(
            'validated',
            'pro.notif.stat.commission_validated',
            'Validée',
            num(meta['commissionValidated']),
            'success',
          ),
          moneyStat(
            'pending',
            'pro.notif.stat.commission_pending_short',
            'En attente',
            num(meta['commissionPending']),
            'neutral',
          ),
          countStat('orders', 'pro.notif.stat.orders', 'Commandes', num(meta['orders'])),
          trendStat(commission, previous),
        ]),
      };
    }

    case 'clients_digest':
      return {
        ...base,
        tone: 'info',
        icon: 'users',
        title: k('pro.notif.clients_digest.title', 'Vos clients cette semaine'),
        body: topClientBody(meta),
        period: periodOf(meta['periodStart'], 'week'),
        stats: compact([
          countStat('clients', 'pro.notif.stat.clients', 'Clients', num(meta['clients'])),
          countStat(
            'active',
            'pro.notif.stat.clients_active',
            'Actifs',
            num(meta['activeClients']),
            'success',
          ),
          countStat(
            'new',
            'pro.notif.stat.clients_new',
            'Nouveaux',
            num(meta['newClients']),
            'brand',
          ),
          moneyStat(
            'revenue',
            'pro.notif.stat.revenue',
            'Chiffre',
            num(meta['revenueTtc']),
            'money',
          ),
        ]),
      };

    case 'formation_video_published': {
      const videos = num(meta['videosCount']) ?? 1;
      const single = videos <= 1;
      return {
        ...base,
        tone: 'learning',
        icon: 'video',
        title: single
          ? k('pro.notif.formation_video.title_one', 'Nouvelle vidéo de formation')
          : k('pro.notif.formation_video.title_many', '{{videos}} nouvelles vidéos de formation', {
              videos,
            }),
        body: single
          ? k('pro.notif.formation_video.body_one', '{{videoTitle}}', {
              videoTitle: str(meta['videoTitle']) ?? '',
            })
          : k('pro.notif.formation_video.body_many', 'Chapitre : {{chapterTitle}}', {
              chapterTitle: str(meta['chapterTitle']) ?? '',
            }),
        thumbnailUrl: str(meta['thumbnailUrl']),
        stats: compact([
          textStat('chapter', 'pro.notif.stat.chapter', 'Chapitre', single ? str(meta['chapterTitle']) : null),
          durationStat(num(meta['durationSeconds'])),
        ]),
      };
    }

    case 'product_published': {
      const products = num(meta['productsCount']) ?? 1;
      const names = strList(meta['sampleNames']);
      const single = products <= 1;
      return {
        ...base,
        tone: 'brand',
        icon: 'new-product',
        title: single
          ? k('pro.notif.product_published.title_one', 'Nouveau produit au catalogue')
          : k('pro.notif.product_published.title_many', '{{products}} nouveaux produits au catalogue', {
              products,
            }),
        body: names.length
          ? k('pro.notif.product_published.body', '{{names}}', {
              names: names.slice(0, 3).join(' · '),
            })
          : null,
        stats: compact([
          countStat('products', 'pro.notif.stat.products', 'Produits', single ? null : products, 'brand'),
        ]),
      };
    }

    case 'product_requests_resolved': {
      const fulfilled = num(meta['fulfilled']) ?? 0;
      const unavailable = num(meta['unavailable']) ?? 0;
      return {
        ...base,
        tone: fulfilled > 0 ? 'success' : 'neutral',
        icon: 'inbox',
        title: k('pro.request.notif_title', 'Réponse à vos demandes de produits'),
        body: str(meta['devisCode'])
          ? k('pro.notif.product_requests.body', 'Devis {{code}}', {
              code: str(meta['devisCode']) ?? '',
            })
          : null,
        stats: compact([
          countStat(
            'fulfilled',
            'pro.notif.stat.fulfilled',
            'Ajoutés',
            fulfilled || null,
            'success',
          ),
          countStat(
            'unavailable',
            'pro.notif.stat.unavailable',
            'Indisponibles',
            unavailable || null,
            'danger',
          ),
        ]),
      };
    }

    default:
      return base;
  }
}

// ------------------------------------------------------------------
// Regroupement par jour (la cloche affiche « Aujourd'hui / Hier / … »)
// ------------------------------------------------------------------

export type ProNotificationDayBucket = 'today' | 'yesterday' | 'earlier';

/**
 * Range une date de notification dans un seau de jour, en heure LOCALE.
 * On compare des jours calendaires, pas des deltas de 24 h : une notification
 * de 23 h hier reste « Hier » quand on la lit à 1 h du matin.
 */
export function proNotificationDayBucket(
  createdAt: string,
  now: Date,
): ProNotificationDayBucket {
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return 'earlier';
  const days = calendarDayDiff(now, created);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return 'earlier';
}

function calendarDayDiff(later: Date, earlier: Date): number {
  const a = Date.UTC(later.getFullYear(), later.getMonth(), later.getDate());
  const b = Date.UTC(earlier.getFullYear(), earlier.getMonth(), earlier.getDate());
  return Math.round((a - b) / 86_400_000);
}

// ------------------------------------------------------------------
// Helpers privés
// ------------------------------------------------------------------

const COMMANDE_STATUS_FALLBACK: Record<
  CommandeStatus,
  { title: string; body: string }
> = {
  en_attente: {
    title: 'Commande en attente',
    body: 'La commande {{code}} attend d’être confirmée.',
  },
  confirmee: {
    title: 'Commande confirmée',
    body: 'La commande {{code}} est confirmée et part en préparation.',
  },
  emballee: {
    title: 'Commande emballée',
    body: 'La commande {{code}} est prête, elle part bientôt.',
  },
  expediee: {
    title: 'Commande expédiée',
    body: 'La commande {{code}} est en route vers votre client.',
  },
  livree: {
    title: 'Commande livrée',
    body: 'La commande {{code}} a été livrée. Votre commission est en cours de validation.',
  },
  retournee: {
    title: 'Commande retournée',
    body: 'La commande {{code}} a été retournée.',
  },
  rejetee: {
    title: 'Commande rejetée',
    body: 'La commande {{code}} a été rejetée.',
  },
};

function isKnownKind(type: string): type is ProNotificationKind {
  return (PRO_NOTIFICATION_TYPES as ReadonlyArray<string>).includes(type);
}

function k(
  key: string,
  fallback: string,
  vars?: Record<string, string | number>,
): ProNotificationL10n {
  return vars ? { key, fallback, vars } : { key, fallback };
}

function progress(status: CommandeStatus): ProNotificationOrderProgress {
  const step = commandeFlowStep(status);
  return {
    status,
    step,
    total: COMMANDE_FLOW.length,
    interrupted: step === null,
  };
}

function periodOf(
  raw: unknown,
  granularity: 'month' | 'week',
): ProNotificationPeriod | null {
  const start = str(raw);
  // On veut un vrai `YYYY-MM-DD` : l'UI construit une Date dessus.
  if (!start || !/^\d{4}-\d{2}-\d{2}$/.test(start)) return null;
  return { start, granularity };
}

function amountOf(
  raw: unknown,
  direction: 'in' | 'out' | 'neutral',
): { value: number; direction: 'in' | 'out' | 'neutral' } | null {
  const value = num(raw);
  return value === null ? null : { value, direction };
}

function refBody(
  meta: Record<string, unknown>,
  key: string,
  fallback: string,
): ProNotificationL10n | null {
  const ref = str(meta['referenceCode']);
  return ref ? k(key, fallback, { ref }) : null;
}

function topClientBody(meta: Record<string, unknown>): ProNotificationL10n | null {
  const name = str(meta['topClientName']);
  if (!name) return null;
  return k('pro.notif.clients_digest.body', 'Meilleur client : {{name}}', { name });
}

function moneyStat(
  id: string,
  key: string,
  fallback: string,
  value: number | null,
  tone: ProNotificationTone = 'neutral',
): ProNotificationStat | null {
  if (value === null) return null;
  return { id, label: k(key, fallback), kind: 'money', value, tone };
}

function countStat(
  id: string,
  key: string,
  fallback: string,
  value: number | null,
  tone: ProNotificationTone = 'neutral',
): ProNotificationStat | null {
  if (value === null) return null;
  return { id, label: k(key, fallback), kind: 'count', value, tone };
}

function textStat(
  id: string,
  key: string,
  fallback: string,
  value: string | null,
): ProNotificationStat | null {
  if (!value) return null;
  return { id, label: k(key, fallback), kind: 'text', value };
}

function durationStat(seconds: number | null): ProNotificationStat | null {
  if (seconds === null || seconds <= 0) return null;
  const minutes = Math.max(1, Math.round(seconds / 60));
  return {
    id: 'duration',
    label: k('pro.notif.stat.duration', 'Durée'),
    kind: 'text',
    value: `${minutes} min`,
  };
}

/**
 * Évolution vs la période précédente. `null` quand la base est nulle : une
 * progression « +∞ % » depuis 0 DT n'apprend rien (même règle que
 * `percentChange` dans statistics.ts).
 */
function trendStat(current: number, previous: number | null): ProNotificationStat | null {
  if (previous === null || previous === 0) return null;
  const delta = ((current - previous) / Math.abs(previous)) * 100;
  const rounded = Math.round(delta);
  return {
    id: 'trend',
    label: k('pro.notif.stat.vs_previous', 'vs mois précédent'),
    kind: 'percent',
    value: rounded,
    tone: rounded >= 0 ? 'success' : 'danger',
    trend: rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat',
  };
}

function compact(
  stats: Array<ProNotificationStat | null>,
): ProNotificationStat[] {
  return stats.filter((s): s is ProNotificationStat => s !== null);
}

function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function strList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string' && v.trim() !== '');
}

function asCommandeStatus(value: unknown): CommandeStatus | null {
  const s = str(value);
  if (!s) return null;
  return s in COMMANDE_STATUS_ICON ? (s as CommandeStatus) : null;
}

/**
 * Repli de formatage pour le corps du bilan mensuel : la vraie mise en forme
 * (`1 234,567 DT`) se fait dans l'UI, mais le fallback français doit rester
 * lisible si la clé i18n manque.
 */
function formatDtFallback(value: number): string {
  return `${value.toFixed(3).replace('.', ',')} DT`;
}
